// The Recovery Builder — guided luxury showroom quiz config.
const U = (id, w = 1100) =>
  `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=80&w=${w}`;
const P = (id, w = 1100) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;

const IMG = {
  homeSauna: U("1583416750470-965b2707b355"),
  saunaWater: U("1713270176378-45fbf4a27099"),
  barrel: U("1759302354886-f2c37dd3dd8c"),
  barrelGarden: P("9638304"),
  cold: U("1713270281683-2a6f0ef76a9c"),
  hotSunset: P("19737856"),
  hotWood: P("30302449"),
  gym: U("1534438327276-14e5300c3a48"),
  gymWide: P("33966785"),
  spaChairs: U("1693578538512-fc66f318c833"),
  spaAmbient: P("35495081"),
  spaRobe: U("1630595271375-5073a6c0638b"),
  infrared: U("1583417657209-d3dd44dc9c09"),
  traditional: U("1660735698223-23a4edefa264"),
  woodStove: U("1728404259075-209cfb5bb89c"),
  cabinForest: P("34155276"),
  cabinModern: P("36818202"),
  livingRoom: U("1583847268964-b28dc8f51f92"),
};

export const QUESTIONS = [
  {
    key: "location", type: "image",
    q: "Where are you building your recovery space?",
    hint: "This shapes everything — placement, durability and design.",
    options: [
      { value: "home", label: "Home", sub: "A dedicated indoor sanctuary", image: IMG.livingRoom },
      { value: "backyard", label: "Backyard", sub: "Outdoor heat under open sky", image: IMG.barrelGarden },
      { value: "gym", label: "Gym", sub: "A recovery amenity for members", image: IMG.gymWide },
      { value: "spa", label: "Spa", sub: "An elevated guest experience", image: IMG.spaAmbient },
      { value: "airbnb", label: "Airbnb", sub: "A standout guest feature", image: IMG.hotSunset },
      { value: "wellness-room", label: "Wellness Room", sub: "A private reset space", image: IMG.spaChairs },
      { value: "not-sure", label: "Not sure yet", sub: "Help me explore the options", image: IMG.saunaWater },
    ],
  },
  {
    key: "build", type: "image",
    q: "What are you looking to build?",
    hint: "Choose the heart of your setup — we'll complete it for you.",
    options: [
      { value: "sauna", label: "Sauna only", sub: "Deep, restorative heat", image: IMG.homeSauna },
      { value: "cold-plunge", label: "Cold plunge only", sub: "A focused cold reset", image: IMG.cold },
      { value: "hot-tub", label: "Hot tub only", sub: "Warm-water relaxation", image: IMG.hotWood },
      { value: "sauna-cold-plunge", label: "Sauna + cold plunge", sub: "The contrast ritual", image: IMG.traditional },
      { value: "sauna-hot-tub", label: "Sauna + hot tub", sub: "Heat & soak retreat", image: IMG.hotSunset },
      { value: "full-suite", label: "Full recovery suite", sub: "The complete experience", image: IMG.spaRobe },
      { value: "help", label: "Help me choose", sub: "Guide me to the right mix", image: IMG.cabinForest },
    ],
  },
  {
    key: "experience", type: "image",
    q: "What kind of experience do you want?",
    hint: "The feeling you're chasing guides the build.",
    options: [
      { value: "deep-heat", label: "Deep heat sauna experience", sub: "Intense, traditional warmth", image: IMG.woodStove },
      { value: "daily-relaxation", label: "Daily relaxation", sub: "An everyday wind-down ritual", image: IMG.spaChairs },
      { value: "contrast", label: "Contrast therapy setup", sub: "Hot-to-cold recovery cycles", image: IMG.cold },
      { value: "outdoor-luxury", label: "Outdoor luxury retreat", sub: "A private retreat under the sky", image: IMG.hotSunset },
      { value: "compact-indoor", label: "Compact indoor recovery", sub: "Space-conscious and serene", image: IMG.infrared },
      { value: "commercial", label: "Commercial wellness amenity", sub: "Built for high-traffic use", image: IMG.gym },
      { value: "guest-upgrade", label: "Guest experience upgrade", sub: "A memorable stay feature", image: IMG.barrel },
    ],
  },
  {
    key: "saunaStyle", type: "image",
    q: "What sauna style are you drawn to?",
    hint: "Pick the look and heat that speaks to you.",
    show: (a) => a.build !== "cold-plunge" && a.build !== "hot-tub",
    options: [
      { value: "traditional", label: "Traditional sauna", sub: "Classic löyly & steam", image: IMG.traditional },
      { value: "infrared", label: "Infrared sauna", sub: "Gentle, energy-efficient heat", image: IMG.infrared },
      { value: "barrel", label: "Barrel sauna", sub: "Iconic outdoor silhouette", image: IMG.barrel },
      { value: "cabin", label: "Cabin sauna", sub: "Modern & spacious", image: IMG.cabinModern },
      { value: "outdoor-cedar", label: "Outdoor cedar sauna", sub: "Warm, weather-ready cedar", image: IMG.cabinForest },
      { value: "indoor", label: "Indoor sauna", sub: "Seamless inside your home", image: IMG.homeSauna },
      { value: "help", label: "Help me choose", sub: "Recommend a style for me", image: IMG.saunaWater },
    ],
  },
  {
    key: "people", type: "icon",
    q: "How many people should it fit?",
    hint: "Capacity drives the size and heater power.",
    options: [
      { value: "solo", label: "Solo", sub: "Just for you", icon: "User" },
      { value: "2", label: "2 people", sub: "Intimate & efficient", icon: "Users" },
      { value: "3-4", label: "3 to 4 people", sub: "Family-sized", icon: "Users" },
      { value: "5-6", label: "5 to 6 people", sub: "Entertain in comfort", icon: "UsersRound" },
      { value: "7+", label: "7+ people", sub: "Large gatherings", icon: "UsersRound" },
      { value: "commercial", label: "Commercial use", sub: "Continuous, high-traffic", icon: "Building2" },
    ],
  },
  {
    key: "placement", type: "icon",
    q: "Where will it be placed?",
    hint: "Placement decides indoor vs. weather-ready builds.",
    options: [
      { value: "indoors", label: "Indoors", sub: "Inside the home", icon: "Home" },
      { value: "outdoors", label: "Outdoors", sub: "Open-air installation", icon: "Trees" },
      { value: "garage", label: "Garage", sub: "Converted utility space", icon: "Warehouse" },
      { value: "basement", label: "Basement", sub: "Lower-level retreat", icon: "ArrowDownToLine" },
      { value: "patio", label: "Patio / backyard", sub: "Deck or garden", icon: "Sun" },
      { value: "commercial", label: "Commercial space", sub: "Facility installation", icon: "Building2" },
    ],
  },
  {
    key: "priority", type: "icon",
    q: "What matters most?",
    hint: "We'll weight your recommendation toward this.",
    options: [
      { value: "luxury", label: "Luxury design", sub: "Premium materials & finish", icon: "Gem" },
      { value: "easy-setup", label: "Easy setup", sub: "Simple install & use", icon: "Wrench" },
      { value: "outdoor-durability", label: "Outdoor durability", sub: "Built for the elements", icon: "CloudRain" },
      { value: "commercial-durability", label: "Commercial durability", sub: "Heavy-duty longevity", icon: "ShieldCheck" },
      { value: "value", label: "Best value", sub: "Smartest cost-to-quality", icon: "BadgePercent" },
      { value: "complete-experience", label: "Complete experience", sub: "Every detail included", icon: "Sparkles" },
    ],
  },
  {
    key: "budget", type: "icon",
    q: "What investment range are you planning for?",
    hint: "We'll match setups to your range — no pressure.",
    options: [
      { value: "under-5k", label: "Under $5,000", sub: "Entry into recovery", icon: "Wallet" },
      { value: "5-10k", label: "$5,000 – $10,000", sub: "Quality home setup", icon: "Wallet" },
      { value: "10-20k", label: "$10,000 – $20,000", sub: "Premium & complete", icon: "CreditCard" },
      { value: "20-40k", label: "$20,000 – $40,000", sub: "Luxury suite territory", icon: "Gem" },
      { value: "40k-plus", label: "$40,000+", sub: "Flagship & commercial", icon: "Crown" },
      { value: "not-sure", label: "Not sure yet", sub: "Show me the range", icon: "HelpCircle" },
    ],
  },
];
